import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { isAxiosError } from 'axios';
import { subHours } from 'date-fns';

import { instance } from '@/interfaces';
import { userInfoAtom } from '@/interfaces/member';
import { deviceDataPeriodAtom } from '@/components/devices/DeviceDetail';
import { selectedDeviceAtom } from '@/components/common/MapView';

import type { Getter } from 'jotai';
import type { AtomWithQueryResult } from 'jotai-tanstack-query';
import type { UserInfo } from '@/@types/user';
import type { Device, Period } from '@/@types/device';
import type { APIResponse } from '@/@types/types';
import type { AuditLog, AuditLogRow } from '@/@types/audit';

export const auditLogPeriodAtom = atom<Period>({ from: subHours(new Date(), 24), to: new Date() });

export const auditLogAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['AUDIT_LOG', get(userInfoAtom), get(auditLogPeriodAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue, auditPeriodAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    const { from, to } = auditPeriodAtomValue as Period;

    const data = await getAuditLog(userInfo.site[0].id, from, to);
    const dataWithId: AuditLogRow[] = data.map((row) => ({ ...row, id: row.timestamp }));

    return dataWithId;
  },
}));
async function getAuditLog(siteId: string, from: Date, to: Date) {
  try {
    const { data } = await instance.get<APIResponse<AuditLog[]>>(`/audit/log/site/${siteId}`, { params: { from, to } });
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getAuditLog] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getAuditLog] Axios error message:', err.message);
      }
    } else {
      console.error('[getAuditLog] Unknown error', err);
    }
    return [];
  }
}

export const deviceAuditLogAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['DEVICE_AUDIT_LOG', get(userInfoAtom), get(selectedDeviceAtom), get(deviceDataPeriodAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue, selectedDeviceAtomValue, deviceDataPeriodAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    const { id } = selectedDeviceAtomValue as Device;
    const { from, to } = deviceDataPeriodAtomValue as Period;

    const data = await getDeviceAuditLog(userInfo.site[0].id, id, from, to);

    return data;
  },
}));
async function getDeviceAuditLog(siteId: string, sensorId: string, from: Date, to: Date) {
  try {
    const { data } = await instance.get<APIResponse<AuditLog[]>>(`/audit/log/site/${siteId}`, {
      params: { sensorId, from, to },
    });
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getDeviceAuditLog] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getDeviceAuditLog] Axios error message:', err.message);
      }
    } else {
      console.error('[getDeviceAuditLog] Unknown error', err);
    }
    return [];
  }
}
